import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import {
  createMaterialFilter,
  generateUniqueIds,
} from '@modules/Vector/S/Function/composite/svg.material-composer.helper.ts';
import { MaterialInfo } from '@modules/Vector/S/Function/composite/svg.composite.types.ts';
import {
  addOutlineElement,
  createClippedGroup,
  createCutAndClipPaths,
  ensureDefsElement,
  findCutPath,
  parseViewBox,
  validateMaterialType,
} from '@modules/Vector/S/Function/composite/svg.utils.ts';
import { SvgUtil } from '@modules/Vector/S/Function/util.svg.ts';

/**
 * SVG 재질 합성 클래스
 */
export class SvgMaterialComposer {
  /**
   *
   * 📋 스티커 재질 합성 로직 흐름:
   *
   * 🎯 1단계: 입력 유효성 검증
   *    - materialInfo가 없거나 source가 없는 경우 원본 SVG 그대로 반환
   *    - 이유: 불필요한 처리를 방지하고 graceful degradation 지원
   *
   * 🎯 2단계: SVG 문서 파싱
   *    - DOMParser로 SVG 문자열을 Document 객체로 변환
   *    - 이유: SVG 조작을 위해 DOM API 사용 필요
   *
   * 🎯 3단계: viewBox 파싱
   *    - SVG의 좌표계 정보를 안전하게 추출 (fallback: width/height)
   *    - 이유: 필터와 클리핑의 정확한 좌표 계산을 위해 필요
   *
   * 🎯 4단계: 컷라인 패스 검증
   *    - stroke-dasharray 속성이 있는 path를 우선 찾음
   *    - 이유: 컷라인은 스티커의 외곽선을 정의하므로 필수 요소
   *
   * 🎯 5단계: 재질 이미지 변환
   *    - 외부 URL 이미지를 base64 data URI로 변환
   *    - 이유: SVG에 embed하기 위해 브라우저 호환성 있는 포맷으로 변환
   *
   * 🎯 6단계: 고유 ID 생성
   *    - nanoid로 충돌 없는 ID 생성 (cut, clip, filter 용도별 분리)
   *    - 이유: 여러 SVG 요소가 동시에 처리될 때 ID 충돌 방지
   *
   * 🎯 7단계: defs 요소 준비
   *    - SVG 정의 요소들을 담을 컨테이너 생성/확보
   *    - 이유: 재사용 가능한 요소들(defs)을 한 곳에 모아두기 위함
   *
   * 🎯 8단계: 컷라인 및 클립패스 생성
   *    - 컷라인 path를 defs에 정의하고 클립패스로 활용
   *    - 이유: 재질을 스티커 모양대로 클리핑하기 위한 마스크 생성
   *
   * 🎯 9단계: 재질 필터 생성
   *    - feImage(재질) + feBlend(합성) + 타입별 후처리 필터 체인 구성
   *    - 이유: 재질 이미지와 원본 디자인을 픽셀 단위로 자연스럽게 합성
   *
   * 🎯 10단계: 클립 그룹 생성
   *    - 필터링된 디자인 요소를 클립패스로 마스킹
   *    - 이유: 스티커 외곽선 밖의 재질이 보이지 않도록 제한
   *
   * 🎯 11단계: 아웃라인 추가
   *    - 컷라인을 따라 스트로크 효과 추가
   *    - 이유: 스티커의 외곽선을 최상위에 한번 더 놓아서 명확하게 보이도록 함
   *
   * 🎯 12단계: 결과 반환
   *    - 수정된 SVG를 문자열로 직렬화하여 반환
   *    - 이유: 함수 호출자에게 완성된 SVG 제공
   *
   * 🔧 기술적 고려사항:
   * - 모든 SVG 요소는 적절한 네임스페이스로 생성 (표준 준수)
   * - 메모리 누수를 방지하기 위해 DOM 조작을 최소화
   * - 타입 안전성을 위해 TypeScript 인터페이스 적극 활용
   * - 에러 발생 시 graceful degradation으로 안정성 보장
   */
  static async composeStickerWithMaterial(svgString: string, materialInfo?: MaterialInfo): Promise<string> {
    // 입력 유효성 검증
    if (UtilS.isEmpty(materialInfo) || !materialInfo?.source) return svgString;

    try {
      // 재질 타입 검증 및 안전한 변환
      const validatedMaterialType = validateMaterialType(materialInfo.type);
      const validatedMaterialInfo: MaterialInfo = {
        ...materialInfo,
        type: validatedMaterialType,
      };

      // SVG 문서 파싱
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement as unknown as SVGSVGElement;

      // viewBox 파싱
      const viewBox = parseViewBox(svg);

      // 컷라인 패스 검증
      const cutPath = findCutPath(svg);
      if (!cutPath) {
        throw new Error('컷라인으로 사용할 수 있는 path 요소를 찾을 수 없습니다');
      }

      // 재질 이미지 변환
      const dataUri = await SvgUtil.urlToDataUriCached(validatedMaterialInfo.source);

      // 고유 ID 생성
      const ids = generateUniqueIds();

      // defs 요소 준비
      const defs = ensureDefsElement(svg, doc);

      // 컷라인 및 클립패스 생성
      createCutAndClipPaths(doc, cutPath, ids, defs);

      // 재질 필터 생성
      createMaterialFilter(doc, viewBox, dataUri, validatedMaterialInfo.type, ids, defs);

      // 클립 그룹 생성
      createClippedGroup(doc, svg, ids, { source: 'design' });

      // 아웃라인 추가
      addOutlineElement(doc, svg, cutPath, ids);

      // 최종 SVG 문자열 반환
      return new XMLSerializer().serializeToString(doc);
    } catch (error) {
      console.error('스티커 재질 합성 실패:', error);
      // 에러 발생 시 원본 SVG 반환 (graceful degradation)
      return svgString;
    }
  }
}
